import { mergeClassnames } from '@porterin/component-library/src/utils/mergeClassnames';
import React, { useEffect } from 'react';

export type Position =
  | 'center'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom-center';

type modalOptions = {
  isOpen: boolean;
  showOverlay?: boolean;
  position?: Position;
};

type interactions = {
  onClose?: () => void;
};

type identifiers = {
  id: string;
  className?: string;
};

interface ModalProps {
  identifiers: identifiers;
  modalOptions: modalOptions;
  interactions: interactions;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = props => {
  // Disable background scroll when modal is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (props.modalOptions.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [props.modalOptions.isOpen]);

  if (!props.modalOptions.isOpen) return null;

  const {
    identifiers: { id, className = '' },
    modalOptions: { showOverlay = true, position = 'center' },
    interactions: { onClose = () => {} },
    children,
  } = props;

  const positionClasses = {
    'center': 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
    'top-left': 'top-5 left-5',
    'top-right': 'top-5 right-5',
    'bottom-left': 'bottom-5 left-5',
    'bottom-right': 'bottom-5 right-5',
    'bottom-center': 'bottom-0 left-1/2 transform -translate-x-1/2 w-full',
  };

  return (
    <div
      id={id}
      className={mergeClassnames(
        'fixed inset-0 z-[150] flex items-center justify-center',
        className,
      )}
    >
      {showOverlay && (
        <div
          id={`overlay-${id}`}
          className="absolute inset-0 bg-black bg-opacity-50"
          role="button"
          tabIndex={0}
          aria-label="Close modal"
          onClick={onClose}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              onClose();
            }
          }}
        />
      )}

      <div
        id={`content-${id}`}
        className={mergeClassnames(
          'absolute rounded-lg max-h-[100dvh] overflow-hidden',
          positionClasses[position],
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;
